<template>
  <div class="music-wrap">
    <!-- Background Particles -->
    <div class="particles">
      <div class="note n1">🎵</div>
      <div class="note n2">🎶</div>
      <div class="note n3">🎼</div>
      <div class="note n4">🎹</div>
      <div class="star s1">✨</div>
      <div class="star s2">⭐</div>
    </div>

    <!-- Header -->
    <div class="header">
      <button class="back-btn" @click="goBack">← بازگشت</button>
      <h1 class="title">استودیوی موسیقی 🎻</h1>
    </div>

    <div class="stage-container">
      <!-- Spotlight Effect -->
      <div class="spotlight"></div>
      
      <!-- Singer / AI Section -->
      <div class="singer-section">
        <div class="singer-avatar" :class="{ singing: isSinging }">
          <span class="avatar-emoji">🎤</span>
          <div class="sound-waves" v-if="isSinging">
            <span></span><span></span><span></span><span></span>
          </div>
        </div>
        
        <div class="lyrics-card" v-if="lyrics">
          <p class="lyrics-text" v-html="formattedLyrics"></p>
        </div>
        
        <div class="input-controls">
          <input 
            v-model="childName" 
            placeholder="هرچی میخوای بگو آهنگش کنم :)" 
            class="name-input"
            :disabled="loading"
            @keyup.enter="generateSong" 
          />
          <button 
            class="magic-btn" 
            @click="generateSong"
            :disabled="loading || !childName.trim()"
          >
            <span v-if="loading" class="spinner"></span>
            <span v-else>بریم بخونیم! ✨</span>
          </button>
        </div>
      </div>

      <!-- Professional Piano -->
      <div class="piano-wrapper">
        <div class="piano-board">
          <div 
            v-for="note in notes" 
            :key="note.id"
            class="piano-key" 
            :class="[note.type, { active: activeKeys.has(note.id) }]"
            :data-note="note.label"
            :data-key="note.pcKey.toUpperCase()"
            @mousedown="playNote(note)"
            @touchstart.prevent="playNote(note)"
            @mouseup="stopNote(note)"
            @touchend.prevent="stopNote(note)"
            @mouseleave="stopNote(note)"
          >
            <span class="key-label">{{ note.label }}</span>
            <span class="pc-key-hint">{{ note.pcKey.toUpperCase() }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onUnmounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useMutation } from '@vue/apollo-composable'
import { SUBMIT_MUSIC_MUTATION, GENERATE_MUSIC_LYRICS_MUTATION } from '../api/mutations'

const router = useRouter()
const route = useRoute()
const slug = route.params.slug

const childName = ref('')
const lyrics = ref('')
const loading = ref(false)
const isSinging = ref(false)
const activeKeys = reactive(new Set())

// Mutations
const { mutate: submitMusic } = useMutation(SUBMIT_MUSIC_MUTATION)
const { mutate: generateLyrics } = useMutation(GENERATE_MUSIC_LYRICS_MUTATION)

// Audio Context
let audioCtx = null
const oscillators = {} // Map to track active oscillators by note ID

// Extended Note Configuration (1.5 Octaves + Black Keys)
const notes = [
  { id: 'C4', freq: 261.63, type: 'white', label: 'Do', pcKey: 'a' },
  { id: 'C#4', freq: 277.18, type: 'black', label: '', pcKey: 'w' },
  { id: 'D4', freq: 293.66, type: 'white', label: 'Re', pcKey: 's' },
  { id: 'D#4', freq: 311.13, type: 'black', label: '', pcKey: 'e' },
  { id: 'E4', freq: 329.63, type: 'white', label: 'Mi', pcKey: 'd' },
  { id: 'F4', freq: 349.23, type: 'white', label: 'Fa', pcKey: 'f' },
  { id: 'F#4', freq: 369.99, type: 'black', label: '', pcKey: 't' },
  { id: 'G4', freq: 392.00, type: 'white', label: 'Sol', pcKey: 'g' },
  { id: 'G#4', freq: 415.30, type: 'black', label: '', pcKey: 'y' },
  { id: 'A4', freq: 440.00, type: 'white', label: 'La', pcKey: 'h' },
  { id: 'A#4', freq: 466.16, type: 'black', label: '', pcKey: 'u' },
  { id: 'B4', freq: 493.88, type: 'white', label: 'Si', pcKey: 'j' },
  { id: 'C5', freq: 523.25, type: 'white', label: 'Do', pcKey: 'k' },
  { id: 'C#5', freq: 554.37, type: 'black', label: '', pcKey: 'o' },
  { id: 'D5', freq: 587.33, type: 'white', label: 'Re', pcKey: 'l' },
  { id: 'D#5', freq: 622.25, type: 'black', label: '', pcKey: 'p' },
  { id: 'E5', freq: 659.25, type: 'white', label: 'Mi', pcKey: ';' }
]

const formattedLyrics = computed(() => {
  return lyrics.value.replace(/\n/g, '<br>')
})

function initAudio() {
  if (!audioCtx) {
    const AudioContext = window.AudioContext || window.webkitAudioContext
    audioCtx = new AudioContext()
  }
  if (audioCtx.state === 'suspended') {
    audioCtx.resume()
  }
}

function playNote(note) {
  initAudio()
  
  if (oscillators[note.id]) return // Already playing

  activeKeys.add(note.id)

  const osc = audioCtx.createOscillator()
  const gainNode = audioCtx.createGain()
  
  // Use 'triangle' or 'sawtooth' for a richer synth sound, 'sine' is very soft
  osc.type = 'triangle' 
  osc.frequency.setValueAtTime(note.freq, audioCtx.currentTime)
  
  // Attack
  gainNode.gain.setValueAtTime(0, audioCtx.currentTime)
  gainNode.gain.linearRampToValueAtTime(0.3, audioCtx.currentTime + 0.05)
  // Decay/Sustain
  gainNode.gain.exponentialRampToValueAtTime(0.1, audioCtx.currentTime + 1.5)

  osc.connect(gainNode)
  gainNode.connect(audioCtx.destination)
  osc.start()

  oscillators[note.id] = { osc, gainNode }
}

function stopNote(note) {
  if (!oscillators[note.id]) return
  
  const { osc, gainNode } = oscillators[note.id]
  
  // Release
  const releaseTime = 0.2
  gainNode.gain.cancelScheduledValues(audioCtx.currentTime)
  gainNode.gain.setValueAtTime(gainNode.gain.value, audioCtx.currentTime)
  gainNode.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + releaseTime)
  osc.stop(audioCtx.currentTime + releaseTime)
  
  delete oscillators[note.id]
  activeKeys.delete(note.id)
}

// PC Keyboard Support
function handleKeyDown(e) {
  if (loading.value) return // Don't play while typing name if loading, but usually name input has focus
  if (e.target.tagName === 'INPUT') return // Don't play if typing in input box

  const key = e.key.toLowerCase()
  const note = notes.find(n => n.pcKey === key)
  if (note) {
    playNote(note)
  }
}

function handleKeyUp(e) {
  const key = e.key.toLowerCase()
  const note = notes.find(n => n.pcKey === key)
  if (note) {
    stopNote(note)
  }
}

function goBack() { router.push(`/room/${slug}`) }

async function generateSong() {
  if (!childName.value || loading.value) return
  loading.value = true
  lyrics.value = ''
  
  try {
    const { data } = await generateLyrics({ data: { childName: childName.value } })
    lyrics.value = (data?.generateMusicLyrics?.lyrics || '').trim()
    
    // Auto-sing
    singLyrics(lyrics.value)
    
    // Save
    await submitMusic({
      data: {
        roomSlug: slug,
        childName: childName.value,
        lyrics: lyrics.value,
        prompt: "Generated Rhyme",
        durationSeconds: 0
      }
    })
  } catch (e) {
    lyrics.value = 'اوه! نتونستم شعر بسازم. دوباره امتحان کن!'
  } finally {
    loading.value = false
  }
}

function singLyrics(text) {
  if (!('speechSynthesis' in window)) return
  window.speechSynthesis.cancel()
  isSinging.value = true
  
  const u = new SpeechSynthesisUtterance(text)
  u.lang = 'fa-IR'
  u.rate = 0.85
  u.pitch = 1.2
  u.onend = () => { isSinging.value = false }
  
  window.speechSynthesis.speak(u)
  playBackingTrack()
}

function playBackingTrack() {
  initAudio()
  // Simple Arpeggio
  const chord = [261.63, 329.63, 392.00, 523.25, 392.00, 329.63]
  const now = audioCtx.currentTime
  
  chord.forEach((freq, i) => {
    const osc = audioCtx.createOscillator()
    const g = audioCtx.createGain()
    osc.frequency.value = freq
    osc.type = 'sine'
    g.gain.value = 0.1
    osc.connect(g)
    g.connect(audioCtx.destination)
    osc.start(now + i * 0.4)
    osc.stop(now + i * 0.4 + 0.3)
  })
}

onMounted(() => {
  window.addEventListener('keydown', handleKeyDown)
  window.addEventListener('keyup', handleKeyUp)
})

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeyDown)
  window.removeEventListener('keyup', handleKeyUp)
  if (audioCtx) audioCtx.close()
  window.speechSynthesis.cancel()
})
</script>

<style scoped>
.music-wrap {
  min-height: 100vh;
  padding: 20px;
  background: radial-gradient(circle at center, #fce7f3 0%, #f472b6 100%);
  font-family: 'Vazirmatn', sans-serif;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  position: relative;
}

/* Header */
.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1000px;
  margin: 0 auto 20px;
  width: 100%;
  position: relative;
  z-index: 10;
}
.back-btn {
  background: rgba(255, 255, 255, 0.5);
  border: 2px solid #fff;
  color: #831843;
  padding: 8px 16px;
  border-radius: 999px;
  font-weight: bold;
  cursor: pointer;
  transition: 0.3s;
}
.back-btn:hover { background: #fff; }
.title {
  color: #831843;
  margin: 0;
  font-size: 24px;
  text-shadow: 0 2px 5px rgba(255,255,255,0.5);
}

/* Stage Layout */
.stage-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between; /* Push piano to bottom */
  width: 100%;
  max-width: 1000px;
  margin: 0 auto;
  position: relative;
  z-index: 5;
}

.spotlight {
  position: absolute;
  top: -100px;
  left: 50%;
  transform: translateX(-50%);
  width: 300px;
  height: 600px;
  background: radial-gradient(ellipse at top, rgba(255,255,255,0.4) 0%, transparent 70%);
  pointer-events: none;
  z-index: -1;
}

/* Singer Section */
.singer-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  width: 100%;
  max-width: 500px;
  margin-top: 20px;
}

.singer-avatar {
  width: 120px;
  height: 120px;
  background: #fff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 60px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.15);
  border: 4px solid #fff;
  position: relative;
  transition: transform 0.3s ease;
}
.singer-avatar.singing {
  animation: bounce 0.6s infinite alternate cubic-bezier(0.4, 0, 0.2, 1);
  border-color: #fbcfe8;
}

/* Sound Waves Animation */
.sound-waves {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
  height: 100%;
  border-radius: 50%;
  pointer-events: none;
}
.sound-waves span {
  position: absolute;
  top: 50%; left: 50%;
  transform: translate(-50%, -50%);
  border: 2px solid rgba(255, 255, 255, 0.8);
  width: 100%; height: 100%;
  border-radius: 50%;
  animation: ripple 2s infinite linear;
  opacity: 0;
}
.sound-waves span:nth-child(2) { animation-delay: 0.5s; }
.sound-waves span:nth-child(3) { animation-delay: 1.0s; }
.sound-waves span:nth-child(4) { animation-delay: 1.5s; }

@keyframes ripple {
  0% { width: 100%; height: 100%; opacity: 0.8; border-width: 4px; }
  100% { width: 250%; height: 250%; opacity: 0; border-width: 0px; }
}

.lyrics-card {
  background: rgba(255, 255, 255, 0.9);
  padding: 20px;
  border-radius: 20px;
  width: 100%;
  text-align: center;
  box-shadow: 0 8px 24px rgba(0,0,0,0.05);
  animation: slideUp 0.5s ease;
}
.lyrics-text {
  margin: 0;
  font-size: 18px;
  line-height: 1.8;
  color: #831843;
}

.input-controls {
  display: flex;
  gap: 10px;
  width: 100%;
  flex-wrap: wrap;
}

.name-input {
  flex: 1;
  min-width: 150px;
  padding: 12px 16px;
  border-radius: 12px;
  border: 2px solid #fff;
  background: rgba(255,255,255,0.6);
  font-family: inherit;
  font-size: 16px;
  text-align: center;
  outline: none;
  transition: 0.3s;
}
.name-input:focus {
  background: #fff;
  border-color: #ec4899;
  box-shadow: 0 4px 12px rgba(236, 72, 153, 0.2);
}

.magic-btn {
  background: linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%);
  color: white;
  border: none;
  border-radius: 12px;
  padding: 0 24px;
  font-weight: 800;
  font-size: 16px;
  cursor: pointer;
  box-shadow: 0 4px 15px rgba(236, 72, 153, 0.4);
  transition: transform 0.2s;
  min-width: 140px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.magic-btn:hover:not(:disabled) { transform: scale(1.05); }
.magic-btn:disabled { opacity: 0.7; cursor: not-allowed; background: #9ca3af; box-shadow: none; }

.spinner {
  width: 20px; height: 20px;
  border: 3px solid #fff;
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

/* Piano Wrapper */
.piano-wrapper {
  width: 100%;
  overflow-x: auto; /* Scroll if needed on tiny screens */
  display: flex;
  justify-content: center;
  padding: 20px 0;
  margin-top: 40px;
}

.piano-board {
  position: relative;
  display: flex;
  background: #1f2937;
  padding: 10px 10px 0 10px; /* Top/Side padding */
  border-radius: 0 0 16px 16px;
  box-shadow: 0 20px 40px rgba(0,0,0,0.4), inset 0 2px 4px rgba(255,255,255,0.1);
}
/* Top lid of piano */
.piano-board::before {
  content: '';
  position: absolute;
  top: -15px; left: 0; right: 0;
  height: 15px;
  background: #374151;
  border-radius: 16px 16px 0 0;
  border-bottom: 2px solid #111;
}

.piano-key {
  position: relative;
  border-radius: 0 0 6px 6px;
  cursor: pointer;
  user-select: none;
  transition: all 0.05s ease-out;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  padding-bottom: 8px;
}

/* White Keys */
.piano-key.white {
  width: 50px;
  height: 200px;
  background: linear-gradient(180deg, #fff 0%, #f3f4f6 100%);
  border: 1px solid #9ca3af;
  border-top: none;
  z-index: 1;
  margin: 0 1px;
  box-shadow: inset 0 -4px 4px rgba(0,0,0,0.1);
}
.piano-key.white.active {
  background: #e5e7eb;
  background: linear-gradient(180deg, #f3f4f6 0%, #d1d5db 100%);
  height: 198px; /* Pressed visual */
  border-bottom: 4px solid #9ca3af;
  transform: translateY(2px);
}

/* Black Keys */
/* Using negative margins to position them between white keys */
.piano-key.black {
  width: 34px;
  height: 120px;
  background: linear-gradient(180deg, #1f2937 0%, #000 100%);
  border: 1px solid #000;
  z-index: 2;
  margin-left: -17px; /* Half width overlap */
  margin-right: -17px;
  color: #fff;
  box-shadow: 2px 4px 6px rgba(0,0,0,0.4);
}
.piano-key.black.active {
  background: #333;
  box-shadow: 1px 2px 4px rgba(0,0,0,0.4);
  transform: translateY(2px);
}

.key-label {
  font-size: 12px;
  font-weight: 700;
  color: #94a3b8;
  pointer-events: none;
}
.piano-key.black .key-label { color: #6b7280; display: none; } /* Hide label on black keys to keep clean */

.pc-key-hint {
  position: absolute;
  bottom: 25px;
  font-size: 10px;
  color: #cbd5e1;
  opacity: 0.7;
}
.piano-key.black .pc-key-hint {
  bottom: 10px;
  color: #9ca3af;
}

/* Decoration Particles */
.particles {
  position: fixed;
  top: 0; left: 0; width: 100%; height: 100%;
  pointer-events: none;
  z-index: 0;
  overflow: hidden;
}
.note, .star {
  position: absolute;
  font-size: 30px;
  opacity: 0.3;
  animation: floatAnim 10s infinite linear;
}
.n1 { top: 80%; left: 10%; animation-duration: 8s; }
.n2 { top: 20%; right: 15%; animation-duration: 12s; font-size: 40px; }
.n3 { top: 60%; right: 5%; animation-duration: 10s; }
.n4 { bottom: 10%; left: 20%; animation-duration: 15s; font-size: 35px; }
.s1 { top: 10%; left: 30%; animation: twinkle 4s infinite; }
.s2 { top: 40%; right: 40%; animation: twinkle 5s infinite; }

@keyframes floatAnim {
  0% { transform: translateY(0) rotate(0deg); }
  50% { transform: translateY(-20px) rotate(10deg); }
  100% { transform: translateY(0) rotate(0deg); }
}
@keyframes twinkle {
  0%, 100% { opacity: 0.2; transform: scale(1); }
  50% { opacity: 0.6; transform: scale(1.2); }
}
@keyframes bounce {
  from { transform: translateY(0); }
  to { transform: translateY(-10px); }
}
@keyframes slideUp {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}
@keyframes spin { to { transform: rotate(360deg); } }

/* Mobile Responsiveness */
@media (max-width: 600px) {
  .piano-key.white { width: 38px; height: 160px; }
  .piano-key.black { width: 24px; height: 90px; margin-left: -12px; margin-right: -12px; }
  .singer-avatar { width: 100px; height: 100px; font-size: 50px; }
  .input-controls { flex-direction: column; gap: 12px; }
  .pc-key-hint { display: none; } /* Hide PC hints on mobile */
  .piano-wrapper { justify-content: flex-start; padding-left: 10px; padding-right: 10px; } /* Allow scrolling */
  .title { font-size: 20px; }
}
</style>